/**
 * Base HTTP client with authentication support
 */

import type { Simulation, Topology } from '../types/simulation';
import type { AuthSession, User } from '../types/auth';

const API_BASE_URL = window.location.origin;
const SESSION_KEY = 'auth_session';

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

/**
 * Get stored session from localStorage
 */
function getSession(): AuthSession | null {
  const sessionStr = localStorage.getItem(SESSION_KEY);
  if (!sessionStr) return null;

  try {
    const session = JSON.parse(sessionStr);
    return session;
  } catch {
    return null;
  }
}

/**
 * Get stored JWT token from localStorage
 */
function getAuthToken(): string | null {
  const session = getSession();
  if (!session) return null;

  // Check if token is expired
  if (Date.now() >= session.expiresAt) {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
  return session.token;
}

/**
 * Refresh the access token using the refresh token
 */
async function refreshAccessToken(): Promise<string> {
  // If already refreshing, wait for that request
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const session = getSession();
      if (!session?.refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: session.refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();

      // Update session with new tokens
      const newSession: AuthSession = {
        token: data.token,
        refreshToken: data.refresh_token,
        expiresAt: Date.now() + (720 * 60 * 60 * 1000), // Fallback expiration
      };

      // Try to get actual expiration from JWT
      try {
        const payload = JSON.parse(atob(data.token.split('.')[1]));
        if (payload.exp) {
          newSession.expiresAt = payload.exp * 1000;
        }
      } catch {
        // Use fallback expiration
      }

      localStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
      return data.token;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Make authenticated API request with automatic token refresh on 401
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Skip refresh for auth endpoints to avoid loops
  const isAuthEndpoint = endpoint.includes('/api/auth/');

  const token = getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add auth header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Merge with provided headers
  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  let response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // If 401 and not an auth endpoint, try to refresh token
  if (response.status === 401 && !isAuthEndpoint) {
    try {
      const newToken = await refreshAccessToken();

      // Retry original request with new token
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      });
    } catch (refreshError) {
      // Refresh failed, clear session and redirect to login
      localStorage.removeItem(SESSION_KEY);
      window.location.href = '/';
      throw new Error('Session expired. Please login again.');
    }
  }

  // Handle error responses
  if (!response.ok) {
    const error = await response.json().catch(() => ({
      detail: `HTTP ${response.status}: ${response.statusText}`,
    }));
    throw new Error(error.detail || 'Request failed');
  }

  return response.json();
}

/**
 * Get list of available simulations
 */
export async function getSimulations(): Promise<Simulation[]> {
  return apiRequest<Simulation[]>('/api/simulations');
}

/**
 * Get simulation details
 */
export async function getSimulation(simulationId: string): Promise<Simulation> {
  return apiRequest<Simulation>(`/api/simulations/${simulationId}`);
}

/**
 * Create a new simulation
 */
export async function createSimulation(data: {
  name: string;
  description?: string;
  status?: 'active' | 'maintenance' | 'private';
  allowed_users?: number[] | null;
  ai_models: Array<{
    ai_name: string;
    topology_id: string;
    position?: [number, number, number] | null;
    auto_spawn?: boolean;
  }>;
}): Promise<Simulation> {
  return apiRequest<Simulation>('/api/simulations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update a simulation
 */
export async function updateSimulation(
  simulationId: string,
  data: {
    name?: string;
    description?: string;
    status?: 'active' | 'maintenance' | 'private';
    allowed_users?: number[] | null;
    ai_models?: Array<{
      ai_name: string;
      topology_id: string;
      position?: [number, number, number] | null;
      auto_spawn?: boolean;
    }>;
  }
): Promise<Simulation> {
  return apiRequest<Simulation>(`/api/simulations/${simulationId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a simulation
 */
export async function deleteSimulation(simulationId: string): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/api/simulations/${simulationId}`, {
    method: 'DELETE',
  });
}

/**
 * Get list of topologies
 */
export async function getTopologies(publishedOnly: boolean = true): Promise<Topology[]> {
  return apiRequest<Topology[]>(`/api/topologies?published_only=${publishedOnly}`);
}

/**
 * Get topology details
 */
export async function getTopology(topologyId: string): Promise<Topology> {
  return apiRequest<Topology>(`/api/topologies/${topologyId}`);
}

/**
 * Create a new topology (admin)
 */
export async function createTopology(data: {
  name: string;
  file_path: string;
  description?: string;
}): Promise<Topology> {
  return apiRequest<Topology>('/api/topologies', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update a topology (admin)
 */
export async function updateTopology(
  topologyId: string,
  data: {
    name?: string;
    description?: string;
    published?: boolean;
  }
): Promise<Topology> {
  return apiRequest<Topology>(`/api/topologies/${topologyId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Publish a topology (admin)
 */
export async function publishTopology(topologyId: string): Promise<Topology> {
  return apiRequest<Topology>(`/api/topologies/${topologyId}/publish`, {
    method: 'POST',
  });
}

/**
 * Delete a topology (admin)
 */
export async function deleteTopology(topologyId: string): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/api/topologies/${topologyId}`, {
    method: 'DELETE',
  });
}

/**
 * Admin: Get all users
 */
export async function getAllUsers(): Promise<User[]> {
  return apiRequest<User[]>('/api/admin/users');
}

/**
 * Admin: Get user by ID
 */
export async function getUserById(userId: number): Promise<User> {
  return apiRequest<User>(`/api/admin/users/${userId}`);
}

/**
 * Admin: Update user
 */
export async function updateUser(
  userId: number,
  data: {
    username?: string;
    email?: string;
    roles?: string[];  // Array of role names
    avatar_url?: string;
  }
): Promise<User> {
  return apiRequest<User>(`/api/admin/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Admin: Delete user
 */
export async function deleteUser(userId: number): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/api/admin/users/${userId}`, {
    method: 'DELETE',
  });
}

/**
 * Admin: Get all simulations (including private)
 */
export async function getAllSimulationsAdmin(): Promise<Simulation[]> {
  return apiRequest<Simulation[]>('/api/admin/simulations');
}

/**
 * Admin: Get all topologies (including unpublished)
 */
export async function getAllTopologiesAdmin(): Promise<Topology[]> {
  return apiRequest<Topology[]>('/api/admin/topologies');
}

/**
 * Admin: Create AI user
 */
export async function createAIUser(data: {
  username: string;
  display_name?: string;
}): Promise<User & { ws_token: string }> {
  return apiRequest<User & { ws_token: string }>('/api/admin/users/ai', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Admin: Get API keys for a user
 */
export async function getUserAPIKeys(userId: number): Promise<Array<{
  id: number;
  key: string;
  name: string | null;
  user_id: number;
  created_at: string;
  last_used_at: string | null;
  revoked: boolean;
}>> {
  return apiRequest(`/api/admin/users/${userId}/api-keys`);
}
